import {
    CreatorHeaderHeightPxMobile,
    CreatorHeaderHeightPxDesktop,
} from 'constants/creator';
import { SampleNavHeightPx } from 'constants/navBar';
import React, {
    forwardRef,
    ForwardRefExoticComponent,
    RefAttributes,
} from 'react';
import { Box } from '@landr/maestro';
import { getClonedChild } from '@landr/maestro';
import { css, cva } from '@landr/styled-system/css';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { cx } from '@landr/styled-system/css';
import { token } from '@landr/styled-system/tokens';
import BackgroundImage from './BackgroundImage/BackgroundImage';
import BackgroundOverlay from './BackgroundOverlay/BackgroundOverlay';
import Content from './Content/Content';
import {
    InnerWrapperProps,
    SectionProps,
    StyledSectionProps,
} from './Section.types';

Content.displayName = 'Section.Content';

const backgroundWrapperStyles = css({
    position: 'absolute',
    inset: 'none',
    pointerEvents: 'none',
});

const getSectionStyles = cva({
    base: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        alignContent: 'center',
        maxWidth: 'full',
        position: 'relative',
    },
    variants: {
        minHeight: {
            base: {
                minHeight: 'full',
            },
            hero: {
                minHeight: 'fit-content',
                paddingTop: '64px',
                height: '50vh',
                maxHeight: {
                    base: '800px',
                    md: '2000px',
                },
            },
            fullHeight: {
                minHeight: 'min',
                height: '100vh',
                maxHeight: {
                    base: '800px',
                    md: '2000px',
                },
            },
            fullHeightMinusCreator: {
                minHeight: 'min',
                height: {
                    base: 'calc(100vh - var(--creator-header-height-mobile))',
                    md: 'calc(100vh - var(--creator-header-height-desktop))',
                },
                maxHeight: {
                    base: '800px',
                    md: '2000px',
                },
            },
        },
        offset: {
            base: {},
            header: {
                top: 'calc(-1 * var(--sample-nav-height))',
                marginBottom: 'calc(-1 * var(--sample-nav-height))',
                paddingTop: 'var(--sample-nav-height)',
            },
        },
        position: {
            center: {},
            bottom: {
                alignItems: 'flex-end',
                paddingY: 'inherit',
            },
        },
        variant: {
            primary: {
                backgroundColor: 'transparent',
            },
            secondary: {
                backgroundColor: 'surface.inverted',
            },
            ghost: {
                backgroundColor: 'surface.low',
            },
        },
    },
    defaultVariants: {
        minHeight: 'base',
        offset: 'base',
        position: 'center',
        variant: 'primary',
    },
});

const getInnerWrapperStyles = (boxed: 'sm' | 'md' | 'lg' | 'xl' | 'full') =>
    css({
        width: 'full',
        marginX: 'auto',
        zIndex: 'base',
        padding: 0,
        paddingBlock: 0,
        maxWidth: boxed === 'full' ? 'full' : token(`breakpoints.${boxed}`),
    });

const StyledSection: React.FC<StyledSectionProps> = ({
    ref,
    minHeight,
    offset,
    position,
    variant,
    children,
    ...props
}) => {
    return (
        <Box
            as="section"
            className={cx(
                getSectionStyles({
                    minHeight,
                    offset,
                    position,
                    variant,
                }),
            )}
            style={
                {
                    '--creator-header-height-mobile': `${CreatorHeaderHeightPxMobile}px`,
                    '--creator-header-height-desktop': `${CreatorHeaderHeightPxDesktop}px`,
                    '--sample-nav-height': `${SampleNavHeightPx}px`,
                } as React.CSSProperties
            }
            {...props}
        >
            {children}
        </Box>
    );
};
const InnerWrapper: React.FC<InnerWrapperProps> = ({
    boxed,
    children,
    ...props
}) => (
    <Box
        className={getInnerWrapperStyles(
            boxed as 'sm' | 'md' | 'lg' | 'xl' | 'full',
        )}
        {...props}
    >
        {children}
    </Box>
);

interface SectionSubComponents {
    BackgroundImage: typeof BackgroundImage;
    BackgroundOverlay: typeof BackgroundOverlay;
    Content: typeof Content;
}

type SectionComponent = ForwardRefExoticComponent<
    SectionProps & RefAttributes<HTMLElement>
> &
    SectionSubComponents;

export const Section = forwardRef(
    (
        {
            isFullHeight = false,
            minHeight = 'base',
            isHeader = false,
            variant = 'primary',
            position = 'center',
            boxed = 'xl',
            children,
        },
        ref,
    ) => {
        const { isVisible: isCreatorVisible } = useCreatorContext();

        const childrenArray = React.Children.toArray(children);

        const backgroundImage = getClonedChild(
            childrenArray,
            Section.BackgroundImage,
            {
                key: 'backgroundImage',
            },
        );

        const backgroundOverlay = getClonedChild(
            childrenArray,
            Section.BackgroundOverlay,
            {
                key: 'backgroundOverlay',
            },
        );
        const content = getClonedChild(childrenArray, Section.Content, boxed);

        const fullHeight = isCreatorVisible
            ? 'fullHeightMinusCreator'
            : 'fullHeight';

        return (
            <StyledSection
                ref={ref}
                minHeight={isFullHeight ? fullHeight : minHeight}
                offset={isHeader ? 'header' : 'base'}
                position={position}
                variant={variant}
            >
                <Box className={backgroundWrapperStyles}>
                    {backgroundImage}
                    {backgroundOverlay}
                </Box>
                <InnerWrapper boxed={boxed} children={content} />
            </StyledSection>
        );
    },
) as SectionComponent;

Section.BackgroundImage = BackgroundImage;
Section.BackgroundOverlay = BackgroundOverlay;
Section.Content = Content;
